import { useEffect, useMemo, useState } from 'react'
import { Column, Data, FieldType, FieldValue, FormatType, FieldRow, ModalId, O, SelectField, Table, UIPermission, option, showToast, useUIActions } from '../../../../../features/ui'
import { useAddressLoaders } from '../../../../../features/address/loaders/addressLoaders'
import { Provincia } from '../../../../../services/gob-ar'
import { Company, useSystemActions, useSystemState, useSystemStateHooks } from '../../../../system'
import { useDataTypesHooks } from '../../../../../features/data-types'
import { IIBBProvinceFormModal } from './iibbProvinceFormModal'
import { RemoveIIBBProvinceModal } from './removeIIBBProvinceModal'

export const IIBBPanel = () => {
    const { company } = useSystemState()
    const { userHasPermission } = useSystemStateHooks()
    const { formatCustomValue } = useDataTypesHooks()
    const [provinces, setProvinces] = useState<Provincia[]>([])
    const [selectedTaxId, setSelectedTaxId] = useState<O<number>>()
    const [provinceToRemove, setProvinceToRemove] = useState<O<string>>()
    const addressLoaders = useAddressLoaders()
    const systemActions = useSystemActions()
    const { openModal } = useUIActions()

    const taxIds = company?.taxIds || []
    const selectedTax = taxIds.find(taxId => taxId.taxId === selectedTaxId) || taxIds.find(taxId => taxId.main) || taxIds[0]
    const canEdit = userHasPermission(UIPermission.SAVE_TAXES)
    const provinceOptions = useMemo(() => provinces.map(province => option(province.nombre, province.nombre)), [provinces])

    useEffect(() => {
        addressLoaders().loadAllProvinces().then(setProvinces)
    }, [])

    useEffect(() => {
        if (selectedTax && selectedTax.taxId !== selectedTaxId) setSelectedTaxId(selectedTax.taxId)
    }, [selectedTax, selectedTaxId])

    const updateProvinces = async (newProvinces: string[]) => {
        if (!company || !selectedTax) return
        const updatedCompany: Company = {
            ...company,
            taxIds: company.taxIds.map(taxId => taxId.taxId === selectedTax.taxId
                ? { ...taxId, collectionAgentProvinces: newProvinces }
                : taxId)
        }
        await systemActions().saveCompany(updatedCompany, undefined, true)
        showToast('La configuración de IIBB fue actualizada.')
    }

    const openAddProvince = () => openModal(ModalId.IIBB_PROVINCE_FORM)
    const addProvince = (province: string) => updateProvinces([...currentProvinces, province])
    const openRemoveProvince = (province: string) => {
        setProvinceToRemove(province)
        openModal(ModalId.REMOVE_IIBB_PROVINCE)
    }
    const removeProvince = () => {
        if (provinceToRemove) updateProvinces(currentProvinces.filter(province => province !== provinceToRemove))
        setProvinceToRemove(undefined)
    }

    const currentProvinces = selectedTax?.collectionAgentProvinces || []
    const columns: Column[] = [{
        name: 'province',
        type: FieldType.TEXT,
        label: 'Provincia',
        render: (value?: FieldValue) => <>{value}</>
    }]
    const rows: Data[] = currentProvinces.map(province => ({ province }))

    return (
        <>
            <FieldRow
                id="iibbTaxId"
                label="CUIT"
            >
                <SelectField
                    id="iibbTaxId"
                    value={selectedTax?.taxId.toString()}
                    options={taxIds.map(taxId => option(taxId.taxId.toString(), formatCustomValue(taxId.taxId, FormatType.CUIT_CUIL)))}
                    onChange={value => value && setSelectedTaxId(Number(value))}
                />
            </FieldRow>
            <FieldRow
                id="collectionAgentProvinces"
                label="Régimen de percepción"
                hint="Indicar las provincias donde la empresa actua como agente de percepción"
            >
                <Table
                    columns={columns}
                    rows={rows}
                    rowId="province"
                    readOnly={!canEdit}
                    small
                    createIcon="plus"
                    onCreate={canEdit ? openAddProvince : undefined}
                    onRemove={canEdit ? openRemoveProvince : undefined}
                />
            </FieldRow>
            <IIBBProvinceFormModal
                options={provinceOptions.filter(province => !currentProvinces.includes(province.value))}
                onSubmit={addProvince}
            />
            <RemoveIIBBProvinceModal
                province={provinceToRemove}
                onSubmit={removeProvince}
            />
        </>
    )
}
